import { ArrowNarrowDownIcon, ArrowNarrowUpIcon } from '@vizit/icons';
import { cx } from '@vizit/theme';
import { getScoreColorVariant, serializeScore } from '@vizit/utils';
import { Skeleton } from '../skeleton';
import { Text, TextColor, TextSize, TextWeight } from '../text';
import { innerClasses, skeletonClasses, textColorClasses, textSizeClasses, wrapperClasses } from './styles';
import { type ScoreStickerProps, ScoreStickerSize } from './types';

function ScoreDiffIndicator({ scoreDiff }: { scoreDiff: number }) {
  const isPositive = scoreDiff > 0;
  const absDiff = Math.abs(scoreDiff);

  return (
    <output
      aria-label={`Score ${isPositive ? 'increased' : 'decreased'} by ${absDiff}`}
      className="flex items-center gap-1"
    >
      {isPositive ? (
        <ArrowNarrowUpIcon aria-hidden="true" size={16} className="text-success-700" />
      ) : (
        <ArrowNarrowDownIcon aria-hidden="true" size={16} className="text-error-700" />
      )}
      <Text elementType="span" size={TextSize.Sm} weight={TextWeight.Medium} color={TextColor.Tertiary}>
        {absDiff}
      </Text>
    </output>
  );
}

/**
 * ScoreSticker displays a score value with color-coded background based on score range.
 * Shares thresholds with ScoreBadge.
 *
 * @example
 * <ScoreSticker score={75} size={ScoreStickerSize.Lg} />
 */
function ScoreSticker(props: ScoreStickerProps) {
  const {
    score,
    size = ScoreStickerSize.Sm,
    isProcessing = false,
    isLoading = false,
    scoreDiff,
    className,
    ref,
    ...other
  } = props;

  const scoreLabel = serializeScore(score);
  const colorVariant = getScoreColorVariant(score);
  const shouldShowDiff = size === ScoreStickerSize.Lg && !!scoreDiff;
  const hasWrapper = size !== ScoreStickerSize.Xs;
  const isNa = score === null;

  if (isLoading) {
    return (
      <div ref={ref} {...other}>
        <Skeleton className={cx(skeletonClasses({ size }), className)} variant="square" />
      </div>
    );
  }

  if (!hasWrapper) {
    return (
      <div
        ref={ref}
        className={cx(
          innerClasses({ colorVariant, size }),
          textColorClasses({ colorVariant }),
          isProcessing && 'opacity-50',
          className,
        )}
        aria-label={`Score: ${scoreLabel}`}
        {...other}
      >
        <span className={textSizeClasses({ size, isNa })}>{scoreLabel}</span>
      </div>
    );
  }

  return (
    <div
      ref={ref}
      className={cx(wrapperClasses({ colorVariant, size }), isProcessing && 'opacity-50', className)}
      aria-label={`Score: ${scoreLabel}`}
      {...other}
    >
      <div
        className={cx(
          innerClasses({ colorVariant, size }),
          !shouldShowDiff && 'justify-center',
          textColorClasses({ colorVariant }),
        )}
      >
        {shouldShowDiff && <div className="flex-1" />}
        <span className={textSizeClasses({ size, isNa })}>{scoreLabel}</span>
        {shouldShowDiff && (
          <div className="flex-1 flex items-start justify-center pt-2">
            <ScoreDiffIndicator scoreDiff={scoreDiff} />
          </div>
        )}
      </div>
    </div>
  );
}

export { ScoreSticker };
export { ScoreStickerSize } from './types';
export type { ScoreStickerProps } from './types';
